import { useEffect, useState } from "react";
import {
    Col, Form, message, Row, Image, Typography, Upload, UploadProps,
    GetProp, Space, Card, Input as AntInput,
    Skeleton,
    Tooltip,
    notification,
    Modal
} from "antd";
import { useForm } from "antd/es/form/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircleTwoTone, CloseCircleTwoTone, CopyOutlined, EyeOutlined } from "@ant-design/icons";

import Button from "../../elements/button/component";
import Input from "../../elements/input/component";
import Select from "../../elements/select/component";

import { onHandleS3Delete, onHandleS3Upload } from "../../s3-bucket/components";
import { addUser, editUser, getUserDetailsById, updateCognitoPassword } from "../../../api/userAPI";
import { ENVIRONMENT } from "../../../common/constants";
import heic2any from "heic2any";
import './style.scss';
import { IoIosArrowBack } from "react-icons/io";
import { useResponsive } from "../../../responsive/ResponsiveContext";

const { Text } = Typography;
type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const inputLabelStyle = {
    fontSize: '14px',
    lineHeight: '22px',
    fontWeight: 700,
};

const inputStyle = {
    width: window.innerWidth <= 767 ? '100%' : '85%',
    minHeight: '40px',
    padding: '0 12px',
    fontSize: '14px',
    lineHeight: '22px',
    fontWeight: 400,
    background: '#F3F4F6FF',
    borderRadius: '6px',
    borderWidth: 0,
    outline: 'none'
};

const roleOptions = [
    { label: 'Admin', value: 'ADMIN' },
    { label: 'Seller', value: 'SELLER' }
];

const statusOptions = [
    { label: 'Enable', value: 'ENABLE' },
    { label: 'Disable', value: 'DISABLE' }
];

const UsersAddEdit = () => {
    const [form] = useForm();
    const [updatePasswordform] = useForm();
    // const location: any = useLocation();
    const navigate = useNavigate();
    const { id: userId } = useParams();
    const isEditMode = window.location.pathname.includes('/edit');
    const [imageList, setImageList] = useState<any[]>([]);
    const [previewImage, setPreviewImage] = useState<string>('');
    const [uploadFiles, setUploadFiles] = useState<any[]>([{}]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [userDetails, setUserDetails] = useState<any>(null);
    const [loadingUserDetails, setLoadingUserDetails] = useState<boolean>(false);
    const [password, setPassword] = useState<string>("");
    const [changePasswordModelOpen, setChangePasswordModelOpen] = useState<boolean>(false);
    const [changePasswordLoading, setChangePasswordLoading] = useState<boolean>(false);
    const { isMobile } = useResponsive();

    useEffect(() => {
        const fetchUserDetails = async () => {
            setLoadingUserDetails(true);
            if (userId) {
                const { status, data } = await getUserDetailsById(+userId);
                if (status === 'Success') {
                    setUserDetails(data);
                    if (data.profileImagePath) {
                        setPreviewImage(`https://${ENVIRONMENT}-image.s3.us-east-1.amazonaws.com/${data.profileImagePath}`);
                        setImageList([1]);
                    }
                    form.setFieldsValue(data);
                    updatePasswordform.setFieldValue("passwordEmail", data.email);
                }
            }
            setLoadingUserDetails(false);
        };
        if (isEditMode) {
            fetchUserDetails();
        } else {
            form.setFieldValue('status', 'ENABLE');
        }
    }, [userId]);

    const getBase64 = (file: FileType): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
        });

    const handleImageChange: UploadProps['onChange'] = async ({ file, fileList }: any) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setImageList(fileList);
        setUploadFiles([fileList]);
    };

    const copyText = `
                    Name: ${form.getFieldValue('name')}
                    Email: ${form.getFieldValue('email')}
                    Password: ${isEditMode ? updatePasswordform.getFieldValue('updatePassword') : form.getFieldValue('password')}
                    Portal Link: https://sandbox.nexpyn.com
                    `;

    const handleSubmit = async () => {
        try {
            await form.validateFields();
        } catch (validationError) {
            console.error("Validation failed:", validationError);
            return;
        }
        setIsSubmitting(true);

        try {
            const rawValues = form.getFieldsValue();
            const formValues = Object.fromEntries(
                Object.entries(rawValues).map(([key, value]) =>
                    typeof value === 'string' ? [key, value.trim()] : [key, value]
                )
            );
            const uploadedFile = uploadFiles?.[0]?.[0]?.originFileObj;
            let response;

            if (isEditMode) {
                response = await editUser({ ...formValues, id: userId });
            } else {
                response = await addUser(formValues);
            }

            if (response?.status === 'Success') {
                const newUser = response.data;
                const currentImage = userDetails?.profileImagePath?.split('/')?.pop();
                const newImage = uploadedFile?.name;

                const shouldUpload = Boolean(uploadedFile && (!isEditMode || newImage !== currentImage));

                if (isEditMode && userDetails?.profileImagePath) {
                    const splitpath = userDetails?.profileImagePath?.split("/");
                    await onHandleS3Delete(splitpath[0], splitpath[1], splitpath[2]);
                }

                if (shouldUpload) {
                    await onHandleS3Upload(newUser.id, 'profile-image', uploadFiles);
                    const response = await editUser({ id: userId || newUser.id, profileImagePath: `${newUser.id}/profile-image/${uploadedFile?.name}` });
                    if (response?.status === 'Success') {
                        message.success(`User ${isEditMode ? 'updated' : 'added'} successfully.`);
                    }
                }
                if (!isEditMode) {
                    notification.success({
                        message: <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 0 }}>User created successfully</p>,
                        description: (
                            <>
                                <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 0 }}>User Details</p>
                                <div style={{ fontSize: 15, lineHeight: 2 }}>
                                    <p><strong>Name:</strong> {form.getFieldValue('name')}</p>
                                    <p><strong>Email:</strong> {form.getFieldValue('email')}</p>
                                    <p><strong>Password:</strong> {form.getFieldValue('password')}</p>
                                    <p>
                                        <strong>Portal Link:</strong>{' '}
                                        <a
                                            href="https://sandbox.nexpyn.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#1677ff' }}
                                        >
                                            https://sandbox.nexpyn.com
                                        </a>
                                    </p>
                                    <Row justify={"end"}>
                                        <Button type="primary" onClick={() => navigator.clipboard.writeText(copyText)}>
                                            <CopyOutlined style={{ marginRight: 6 }} />
                                            Copy Details
                                        </Button>
                                    </Row>
                                </div>
                            </>
                        ),
                        placement: 'top',
                        duration: 15,
                        className: 'wide-notification',
                    });
                }
                form.resetFields();
                setImageList([]);
                setPreviewImage('');
                navigate('/user');
            } else if (response?.status === 'Failure') {
                message.error(response.message);
            }
        } catch (error) {
            console.error("User Submit Error:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderSelect = (type: 'Role' | 'Status') => (
        <Select
            showSearch
            options={type === 'Role' ? roleOptions : statusOptions}
            filterOption={(input, option) =>
                option?.label?.toLowerCase()?.includes(input.toLowerCase())
            }
            style={inputStyle}
            getPopupContainer={trigger => trigger.parentElement ?? document.body}
            disabled={!isEditMode && type === 'Status'}
        />
    );

    const sanitizeFilename = (filename: any) => {
        return filename
            .replace(/\s+/g, '_')        // Replace spaces with underscores
            .replace(/[()]/g, '')        // Remove parentheses
            .replace(/[^a-zA-Z0-9._-]/g, ''); // Remove unsafe characters
    };

    const OnhandleUpdateCognitoPassword = async () => {
        try {
            await updatePasswordform.validateFields();
            setChangePasswordLoading(true);

            const formValues = updatePasswordform.getFieldsValue();
            const postData = {
                email: formValues?.passwordEmail,
                newPassword: formValues?.updatePassword
            };

            const { status } = await updateCognitoPassword(postData);

            if (status === "Success") {
                notification.success({
                    message: <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 0 }}>Password updated successfully</p>,
                    description: (
                        <>
                            <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 0 }}>User Details</p>
                            <div style={{ fontSize: 15, lineHeight: 2 }}>
                                <p><strong>Name:</strong> {form.getFieldValue('name')}</p>
                                <p><strong>Email:</strong> {updatePasswordform.getFieldValue('passwordEmail')}</p>
                                <p><strong>Password:</strong> {updatePasswordform.getFieldValue('updatePassword')}</p>
                                <p>
                                    <strong>Portal Link:</strong>{' '}
                                    <a
                                        href="https://sandbox.nexpyn.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#1677ff' }}
                                    >
                                        https://sandbox.nexpyn.com
                                    </a>
                                </p>
                                <Row justify={"end"}>
                                    <Button type="primary" onClick={() => navigator.clipboard.writeText(copyText)}>
                                        <CopyOutlined style={{ marginRight: 6 }} />
                                        Copy Details
                                    </Button>
                                </Row>
                            </div>
                        </>
                    ),
                    placement: 'top',
                    duration: 15,
                    className: 'wide-notification',
                });
                updatePasswordform.resetFields();
                setChangePasswordModelOpen(false);
            }
        } catch (error: any) {
            console.log("update password error:", error);
        } finally {
            setChangePasswordLoading(false);
        }
    };

    const passwordRules = [
        {
            label: "8-character minimum length",
            valid: password.length >= 8,
        },
        {
            label: "Contains at least 1 number",
            valid: /\d/.test(password),
        },
        {
            label: "Contains at least 1 lowercase letter",
            valid: /[a-z]/.test(password),
        },
        {
            label: "Contains at least 1 uppercase letter",
            valid: /[A-Z]/.test(password),
        },
        {
            label: "Contains at least 1 special character",
            valid: /[!@#$%^&*(),.?":{}|<>_\-\\[\]/+=~`';]/.test(password),
        },
    ];

    const renderIcon = (valid: boolean) =>
        valid ? (
            <CheckCircleTwoTone twoToneColor="#52c41a" />
        ) : (
            <CloseCircleTwoTone twoToneColor="#ccc" />
        );
    return (
        <>
            <div style={{
                borderRight: '1px solid rgba(186, 182, 182, 0.14)',
                borderLeft: '1px solid rgba(186, 182, 182, 0.14)',
                background: '#fff',
                padding: 5,
                paddingTop: 20
            }}
            >
                <Link to='/user' style={{ display: 'inline-flex', alignItems: "center", color: '#2c35e0' }} ><IoIosArrowBack /> Back</Link>
            </div>
            <Form
                form={form}
                layout="vertical"
                // style={{ marginTop: '1%' }}
                onFinishFailed={(err) => console.error("Submit failed:", err)}
            >
                <Card
                    className="card-container create-card"
                    style={{ borderTop: 'none', borderRadius: 0 }}
                    title={
                        <Row justify="space-between" align="middle">
                            <Col>
                                <span style={{ fontSize: 25, fontWeight: 600, color: '#323842FF' }}>
                                    {isEditMode ? 'Edit' : 'Create'} User
                                </span>
                            </Col>
                            {!isMobile && <Col>
                                <Space>
                                    <Button
                                        type="default"
                                        style={{ backgroundColor: '#F3F4F6FF', borderColor: '#F3F4F6FF', marginRight: 10 }}
                                        onClick={() => navigate('/user')}>Cancel</Button>
                                    <Button
                                        type="primary"
                                        style={{ backgroundColor: '#636AE8FF', borderColor: '#636AE8FF' }}
                                        htmlType="submit"
                                        loading={isSubmitting}
                                        onClick={handleSubmit}
                                    >
                                        {isEditMode ? 'Update' : 'Create'}
                                    </Button>
                                </Space>
                            </Col>}
                        </Row>
                    }
                >
                    {loadingUserDetails ? <Skeleton.Button
                        style={{ height: "70vh", marginTop: "1.4%" }}
                        active={true}
                        size={'small'}
                        shape={"square"}
                        block={true}

                    /> :
                        <>
                            <Row style={{ gap: 10 }}>
                                <Col style={{ marginTop: 13, marginLeft: "-5px" }}>
                                    <div style={{
                                        background: '#9095a0',
                                        width: 100,
                                        height: 100,
                                        margin: 10,
                                        borderRadius: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {imageList.length && previewImage
                                            ? <Image preview={{ mask: <EyeOutlined /> }} src={previewImage} height={100} style={{ objectFit: 'cover' }} />
                                            : <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24"><path fill="#9095a0" d="M20.37 21.25a.75.75 0 0 1-.75.75H4.38a.75.75 0 0 1-.75-.75c0-4.1 4.5-7.28 8.37-7.28s8.37 3.18 8.37 7.28M17.1 7.11A5.1 5.1 0 1 1 12 2a5.11 5.11 0 0 1 5.1 5.11" stroke="#fff" /></svg>
                                        }
                                    </div>
                                </Col>
                                <Col style={{ marginTop: 30 }}>
                                    <Text>Upload your profile image</Text>
                                    {/* <div style={{ marginTop: 10 }}><Text type="secondary">Your image should be in PNG or JPG format</Text></div> */}
                                    {/* <div><Text type="secondary">Ensure your file is 3 MB or less.</Text></div> */}
                                    <Row style={{ marginTop: 5, gap: 10 }}>
                                        <Upload
                                            accept=".png,.jpg,.jpeg,.heic,.heif,.hevc"
                                            showUploadList={false}
                                            maxCount={1}
                                            disabled={isSubmitting}
                                            beforeUpload={async (file) => {
                                                const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
                                                const heicTypes = ['.heic', '.heif', '.hevc'];

                                                if (heicTypes.includes(ext)) {
                                                    try {
                                                        const convertedBlob = await heic2any({
                                                            blob: file,
                                                            toType: 'image/jpeg',
                                                            quality: 0.9,
                                                        });

                                                        const convertedFile = new File(
                                                            [convertedBlob as Blob],
                                                            sanitizeFilename(file.name.replace(/\.(heic|heif|hevc)$/i, '.jpg')),
                                                            { type: 'image/jpeg' }
                                                        );

                                                        return convertedFile;
                                                    } catch (error) {
                                                        message.error('Failed to convert HEIC/HEIF/HEVC image.', 5);
                                                        return Upload.LIST_IGNORE;
                                                    }
                                                } else {
                                                    const sanitizedFile = new File(
                                                        [file],
                                                        sanitizeFilename(file.name),
                                                        { type: file.type }
                                                    );

                                                    return sanitizedFile;
                                                }
                                            }}
                                            onChange={handleImageChange}
                                        >
                                            <Button disabled={isSubmitting} style={{ fontSize: 12, borderColor: '#636AE8FF', color: '#636AE8FF', marginTop: 10 }}>
                                                Choose Image
                                            </Button>
                                        </Upload>
                                        {imageList.length ?
                                            <Button
                                                disabled={isSubmitting}
                                                onClick={() => {
                                                    setImageList([]);
                                                    setPreviewImage('');
                                                    setUploadFiles([{}]);
                                                }}
                                                style={{ fontSize: 12, color: '#DE3B40', borderColor: '#DE3B40', marginTop: 10 }}
                                            >
                                                Remove Image
                                            </Button> : ''}
                                    </Row>
                                </Col>
                            </Row>
                            <br />
                            <Row gutter={isMobile ? [6, 6] : [24, 24]}>
                                <Col span={isMobile ? 24 : 12}>
                                    <Form.Item
                                        name="name"
                                        label={<span style={inputLabelStyle}>Name</span>}
                                        rules={[{ required: true, whitespace: true, message: "Please enter name" }]}
                                    >
                                        <Input style={{ ...inputStyle }} placeholder="Enter Name" />
                                    </Form.Item>
                                </Col>
                                <Col span={isMobile ? 24 : 12}>
                                    <Form.Item
                                        name="email"
                                        label={<span style={inputLabelStyle}>Email</span>}
                                        rules={[
                                            { required: true, whitespace: true, type: "email", message: "Please enter a valid email address" },
                                        ]}
                                    >
                                        <Input style={inputStyle} placeholder="Enter Email" disabled={isEditMode} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={isMobile ? [6, 6] : [24, 24]}>
                                <Col span={isMobile ? 24 : 12}>
                                    <Form.Item
                                        name="role"
                                        label={<span style={inputLabelStyle}>Role</span>}
                                        rules={[{ required: true, whitespace: true, message: "Please select role" }]}
                                    >
                                        {renderSelect('Role')}
                                    </Form.Item>
                                </Col>
                                <Col span={isMobile ? 24 : 12}>
                                    <Form.Item
                                        name="status"
                                        label={<span style={inputLabelStyle}>Status</span>}
                                    >
                                        {renderSelect('Status')}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={isMobile ? [6, 6] : [24, 24]}>
                                {!isEditMode ?
                                    <Col span={isMobile ? 24 : 12}>
                                        <Form.Item
                                            name="password"
                                            label={<span style={{ fontWeight: 600 }}>Password</span>}
                                            rules={[{ required: true, whitespace: true, message: "Please enter password" }]}
                                        >
                                            <Tooltip
                                                placement="top"
                                                title={
                                                    <div style={{ lineHeight: "1.8" }}>
                                                        <b>Requirements:</b>
                                                        <ul style={{ paddingLeft: 20 }}>
                                                            {passwordRules.map((rule, index) => (
                                                                <li key={index}>
                                                                    {renderIcon(rule.valid)} <span style={{ marginLeft: 8 }}>{rule.label}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                }
                                                styles={{ body: { width: '150%' } }}
                                            >
                                                <AntInput.Password
                                                    placeholder="Enter Password"
                                                    value={password}
                                                    style={inputStyle}
                                                    autoComplete="new-password"
                                                    name="dummy-password-field"
                                                    onChange={(e) => {
                                                        setPassword(e.target.value),
                                                            form.setFieldsValue({ password: e.target.value });
                                                    }}
                                                />
                                            </Tooltip>
                                        </Form.Item>
                                    </Col> :
                                    <Col span={isMobile ? 24 : 12}>
                                        <Form.Item
                                            name=""
                                            label={<span style={{ fontWeight: 600 }}>{ }</span>}
                                        >
                                            <Button
                                                disabled={isSubmitting}
                                                type="primary"
                                                style={{ backgroundColor: '#636AE8FF', borderColor: '#636AE8FF' }}
                                                onClick={() => setChangePasswordModelOpen(true)}
                                            >
                                                Change Password
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                }
                            </Row>
                            {isMobile &&
                                <Row justify="end" align="middle" style={{marginTop:"5px"}}>
                                    <Col>
                                        <Space>
                                            <Button
                                                type="default"
                                                style={{ backgroundColor: '#F3F4F6FF', borderColor: '#F3F4F6FF', marginRight: 10 }}
                                                onClick={() => navigate('/user')}>Cancel</Button>
                                            <Button
                                                type="primary"
                                                style={{ backgroundColor: '#636AE8FF', borderColor: '#636AE8FF' }}
                                                htmlType="submit"
                                                loading={isSubmitting}
                                                onClick={handleSubmit}
                                            >
                                                {isEditMode ? 'Update' : 'Create'}
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            }
                        </>
                    }
                    <Modal
                        title={<h3>Change Password</h3>}
                        footer={false}
                        open={changePasswordModelOpen}
                        onCancel={() => setChangePasswordModelOpen(false)}
                    >
                        <Form layout="vertical" form={updatePasswordform} style={{ marginTop: 15 }} autoComplete="off">
                            <Row gutter={[8, 2]}>
                                <Col span={24}>
                                    <Form.Item
                                        name="passwordEmail"
                                        label={<span style={{ ...inputLabelStyle }}>Email</span>}
                                        style={{ width: '115%' }}
                                    >
                                        <Input disabled />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        name="updatePassword"
                                        label={<span style={{ fontWeight: 600 }}>New Password</span>}
                                        rules={[{ required: true, message: "Please enter new password" }]}
                                        style={{ width: '115%' }}
                                    >
                                        <Tooltip
                                            placement="top"
                                            title={
                                                <div style={{ lineHeight: "1.8" }}>
                                                    <b>Requirements:</b>
                                                    <ul style={{ paddingLeft: 20 }}>
                                                        {passwordRules.map((rule, index) => (
                                                            <li key={index}>
                                                                {renderIcon(rule.valid)} <span style={{ marginLeft: 8 }}>{rule.label}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            }
                                            styles={{ body: { width: '150%' } }}
                                        >
                                            <AntInput.Password
                                                placeholder="Enter new Password"
                                                value={password}
                                                style={inputStyle}
                                                autoComplete="new-password"
                                                name="dummy-password-field"
                                                onChange={(e) => {
                                                    setPassword(e.target.value),
                                                        updatePasswordform.setFieldsValue({ updatePassword: e.target.value });
                                                }}
                                            />
                                        </Tooltip>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify={"end"} style={{ marginTop: 10 }}>
                                <Col>
                                    <Space>
                                        <Button
                                            type="default"
                                            style={{ backgroundColor: '#F3F4F6FF', borderColor: '#F3F4F6FF', marginRight: 10, paddingInline: 65 }}
                                            onClick={() => {
                                                setChangePasswordModelOpen(false);
                                            }}>Cancel</Button>
                                        <Button
                                            type="primary"
                                            style={{ backgroundColor: '#636AE8FF', borderColor: '#636AE8FF' }}
                                            htmlType="submit"
                                            loading={changePasswordLoading}
                                            onClick={OnhandleUpdateCognitoPassword}
                                        >
                                            {'Update New Password'}
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Modal>
                </Card >
            </Form >
        </>
    );
};

export default UsersAddEdit;
